import { queryView, type QueryResultLike } from '@krp/client-runtime'

import { actionView } from '../../../shared/views'
import type { ActionView, MutationResultLike } from '../../../shared/views'
import type {
  AddPhotoVm,
  PairVm,
  PhotoIdentity,
  PhotoPair,
  PhotoRegistry,
  PhotoRegistryVm,
  PhotoSource,
  PhotoStaging,
  PhotoTileVm,
  SendJob,
  SendProgressVm,
  SlotVm,
  SourceProblemVm,
} from './types'

/** Ключ строки «фотография × источник»: по нему отправка находит свой ход и свою кнопку. */
export const slotKey = (target: { photoKey: string | null; stagingId: string | null }, resourceId: string): string =>
  `${target.stagingId !== null ? `staging:${target.stagingId}` : (target.photoKey ?? '')}|${resourceId}`

export const stagingTileId = (stagingId: string): string => `staging:${stagingId}`

const UNCHANGED_TEXT = 'Фото у источника уже лежит — отправлять нечего'

/** Запрос на отправку ушёл, ответа ещё нет. */
export const sendingProgress = (title: string): SendProgressVm => ({ phase: 'sending', text: `Отправляю в «${title}»…`, error: null })

export const unchangedProgress = (): SendProgressVm => ({ phase: 'unchanged', text: UNCHANGED_TEXT, error: null })

/** Отказ самой команды отправки (до задачи): текст сервера дословно. */
export const refusedProgress = (error: { code: string; message: string; correlationId: string | null }): SendProgressVm => ({
  phase: 'failed',
  text: 'Отправка не начата',
  error: { code: error.code, message: error.message, requestId: error.correlationId },
})

/**
 * Ход отправки по задаче службы синхронизации. Слова — по реальному состоянию: «Отправлено» только когда строка задачи завершилась успешно,
 * отказ источника показывается его текстом без пересказа.
 */
export function progressOfJob(job: SendJob, title: string): SendProgressVm {
  const failedRow = job.items.find((item) => item.state === 'failed')
  if (failedRow !== undefined || job.state === 'failed') {
    const message = failedRow?.message ?? job.errorMessage ?? 'Служба синхронизации не назвала причину'
    return {
      phase: 'failed',
      text: `Не отправлено в «${title}»`,
      error: { code: failedRow?.errorCode ?? 'sync-job-failed', message, requestId: failedRow?.requestId ?? null },
    }
  }
  if (job.state === 'completed') {
    return { phase: 'done', text: `Отправлено в «${title}»: источник принял фото. Отметка появится после следующего чтения источника.`, error: null }
  }
  return { phase: 'running', text: job.state === 'queued' ? `Жду очереди отправки в «${title}»…` : `Отправляется в «${title}»…`, error: null }
}

/** Ключ хода удаления: у источника — как у строки отправки, у временной загрузки — ключ плитки. */
export const removalKey = (tile: { id: string }, resourceId: string | null): string => (resourceId === null ? tile.id : `${tile.id}|${resourceId}`)

const DELETE_UNCHANGED_TEXT = 'Фото у источника уже нет — удалять нечего'

/** Запрос на удаление ушёл, ответа ещё нет; `title` — источник либо `null` у временной загрузки. */
export const deletingProgress = (title: string | null): SendProgressVm => ({
  phase: 'sending',
  text: title === null ? 'Убираю загруженное фото…' : `Удаляю из «${title}»…`,
  error: null,
})

export const deleteUnchangedProgress = (): SendProgressVm => ({ phase: 'unchanged', text: DELETE_UNCHANGED_TEXT, error: null })

/**
 * Отказ самой команды удаления (до задачи): текст сервера дословно. «Исход неизвестен» говорит словами, что удаление могло пройти, —
 * повтор несёт тот же ключ, и второго удаления не будет.
 */
export const deleteRefusedProgress = (
  error: { code: string; message: string; correlationId: string | null },
  unknownOutcome: boolean,
): SendProgressVm => ({
  phase: 'failed',
  text: unknownOutcome ? 'Исход удаления неизвестен: оно могло пройти' : 'Удаление не выполнено',
  error: { code: error.code, message: error.message, requestId: error.correlationId },
})

/** Ход удаления из источника по задаче службы синхронизации; слова — по реальному состоянию, отказ источника — его текстом без пересказа. */
export function deleteProgressOfJob(job: SendJob, title: string): SendProgressVm {
  const failedRow = job.items.find((item) => item.state === 'failed')
  if (failedRow !== undefined || job.state === 'failed') {
    const message = failedRow?.message ?? job.errorMessage ?? 'Служба синхронизации не назвала причину'
    return {
      phase: 'failed',
      text: `Не удалено из «${title}»`,
      error: { code: failedRow?.errorCode ?? 'sync-job-failed', message, requestId: failedRow?.requestId ?? null },
    }
  }
  if (job.state === 'completed') {
    return { phase: 'done', text: `Удалено из «${title}»: источник принял удаление. Отметка исчезнет после следующего чтения источника.`, error: null }
  }
  return { phase: 'running', text: job.state === 'queued' ? `Жду очереди удаления из «${title}»…` : `Удаляется из «${title}»…`, error: null }
}

export interface PhotoRegistryVmInput {
  result: QueryResultLike<PhotoRegistry>
  /** Источники фото товара из ответа сервера (`GET …/photo-sources`) в его порядке. */
  sources: readonly PhotoSource[]
  /** Живые временные загрузки товара с сервера (`GET …/photos/staging`). */
  staged: readonly PhotoStaging[]
  /** Ход отправок по ключу строки (`slotKey`). */
  sends: Readonly<Record<string, SendProgressVm>>
  /** Ход удалений по ключу `removalKey`: у источника и у временной загрузки. */
  removals: Readonly<Record<string, SendProgressVm>>
  decision: MutationResultLike<unknown> | null
  /** Идущее «Добавить фото»; `null` — не идёт. */
  adding: AddPhotoVm | null
  /** Отказ чтения списка источников (`GET …/photo-sources`) дословно; без списка отметок и кнопок нет. */
  sourcesError: ActionView['error']
}

const UNKNOWN_REASON = 'Источник сейчас не прочитан: сначала нужно прочитать его снова, иначе неизвестно, есть ли там это фото.'
const SENDING_REASON = 'Отправка уже идёт.'
const DELETING_REASON = 'Удаление уже идёт.'

/** Подпись источника: короткая подпись вида; два источника одного вида различаются именем экземпляра. */
export function sourceLabels(sources: readonly PhotoSource[]): ReadonlyMap<string, string> {
  const perKind = new Map<string, number>()
  for (const source of sources) perKind.set(source.kind, (perKind.get(source.kind) ?? 0) + 1)
  return new Map(
    sources.map((source) => [source.resourceId, (perKind.get(source.kind) ?? 0) > 1 ? `${source.shortTitle} «${source.displayName}»` : source.shortTitle]),
  )
}

function pairReason(pair: PhotoPair): string {
  const parts: string[] = []
  if (pair.hashDistance !== null) parts.push(`отпечатки отличаются на ${String(pair.hashDistance)} из 64`)
  if (pair.cosine !== null) parts.push(`сходство кадров ${String(Math.round(pair.cosine * 100))} %`)
  return parts.length === 0 ? 'Возможно, это одна и та же фотография' : `Возможно, одна фотография: ${parts.join(', ')}`
}

/**
 * Модель окна фото общего товара: фотографии реестра и временные загрузки, под каждой — строка по каждому источнику из ответа сервера. Состояние
 * источника честное: «есть» — фото видели у источника; «неизвестно» — источник не прочитан или его пределы не получены (на «нет» это не похоже и «нет»
 * не означает); «нет» — источник прочитан и фото там нет. Кнопка «Отправить» — только у источника с `supports.add`; подтверждение замены — когда
 * `limits.maxPhotos = 1` и там уже стоит другая картинка.
 */
export function buildPhotoRegistryVm(input: PhotoRegistryVmInput): PhotoRegistryVm {
  const registry = input.result.data
  const labels = sourceLabels(input.sources)
  const labelOf = (resourceId: string): string => labels.get(resourceId) ?? 'Источник'
  const states = new Map((registry?.sources ?? []).map((state) => [state.resourceId, state]))
  const identities = registry?.photos ?? []

  const slotsOf = (tileId: string, photoKey: string | null, stagingId: string | null, here: PhotoIdentity | null): SlotVm[] =>
    input.sources.map((source): SlotVm => {
      const key = slotKey({ photoKey, stagingId }, source.resourceId)
      const state = states.get(source.resourceId)
      const present = here?.occurrences.some((occurrence) => occurrence.resourceId === source.resourceId) === true
      const holdsOne = source.limits?.maxPhotos === 1
      const replaces =
        holdsOne &&
        identities.some((other) => other.photoKey !== photoKey && other.occurrences.some((occurrence) => occurrence.resourceId === source.resourceId))
      const profileUnknown = source.status === 'unknown'
      const unreadable = state === undefined || state.unknown || !source.supports.read
      const slotState = present ? 'present' : profileUnknown || unreadable ? 'unknown' : 'absent'
      const progress = input.sends[key] ?? null
      const sending = progress !== null && (progress.phase === 'sending' || progress.phase === 'running')
      const settled = progress !== null && (progress.phase === 'done' || progress.phase === 'unchanged')
      const removal = input.removals[removalKey({ id: tileId }, source.resourceId)] ?? null
      const deleting = removal !== null && (removal.phase === 'sending' || removal.phase === 'running')
      const deleted = removal !== null && (removal.phase === 'done' || removal.phase === 'unchanged')
      return {
        key,
        resourceId: source.resourceId,
        title: labelOf(source.resourceId),
        state: slotState,
        canAdd: source.supports.add,
        limitNote: holdsOne ? (source.limits?.note ?? null) : null,
        replaces,
        progress,
        canSend: source.supports.add && slotState === 'absent' && !sending && !settled && !deleting,
        canDelete: source.supports.delete && present && !deleted,
        deleteProgress: removal,
        deleteBlockedReason: deleting ? DELETING_REASON : sending ? SENDING_REASON : null,
        blockedReason:
          slotState === 'unknown'
            ? profileUnknown && source.error !== null
              ? `Источник не ответил: ${source.error}`
              : UNKNOWN_REASON
            : sending
              ? SENDING_REASON
              : null,
      }
    })

  // Главное — то, что стоит главным у первого по порядку источника, где главное есть.
  const mainKey =
    input.sources
      .map((source) => identities.find((identity) => identity.occurrences.some((o) => o.resourceId === source.resourceId && o.isMainAtSource)))
      .find((identity) => identity !== undefined)?.photoKey ?? null

  // Загрузка, которая уже стала фотографией реестра (после первой отправки сервер её удалил), отдельной плиткой не показывается.
  const waiting = input.staged.filter((item) => !identities.some((identity) => identity.photoKey === item.photoKey))
  const pending: PhotoTileVm[] = waiting.map((item) => ({
    id: stagingTileId(item.stagingId),
    photoKey: null,
    stagingId: item.stagingId,
    thumbUrl: item.contentUrl,
    isMain: false,
    pending: true,
    slots: slotsOf(stagingTileId(item.stagingId), null, item.stagingId, null),
    removal: input.removals[stagingTileId(item.stagingId)] ?? null,
  }))
  const placed: PhotoTileVm[] = identities.map((identity) => ({
    id: identity.photoKey,
    photoKey: identity.photoKey,
    stagingId: null,
    thumbUrl: identity.previewUrl,
    isMain: identity.photoKey === mainKey,
    pending: false,
    slots: slotsOf(identity.photoKey, identity.photoKey, null, identity),
    removal: null,
  }))

  const thumbs = new Map(placed.map((tile) => [tile.id, tile.thumbUrl]))
  const pairs: PairVm[] = (registry?.similar ?? [])
    .filter((pair) => thumbs.has(pair.keyA) && thumbs.has(pair.keyB))
    .map((pair) => ({
      id: `${pair.keyA}|${pair.keyB}`,
      keyA: pair.keyA,
      keyB: pair.keyB,
      thumbA: thumbs.get(pair.keyA) ?? null,
      thumbB: thumbs.get(pair.keyB) ?? null,
      because: pairReason(pair),
    }))

  const readProblems: SourceProblemVm[] = (registry?.sources ?? [])
    .filter((state) => state.unknown || state.errorMessage !== null)
    .map((state) => ({
      resourceId: state.resourceId,
      title: labelOf(state.resourceId),
      code: state.errorCode,
      message: state.errorMessage ?? 'Источник не прочитан, причина не передана',
    }))
  const profileProblems: SourceProblemVm[] = input.sources
    .filter((source) => source.status === 'unknown')
    .map((source) => ({
      resourceId: source.resourceId,
      title: labelOf(source.resourceId),
      code: null,
      message: source.error ?? 'Пределы фото источника не получены, причина не передана',
    }))

  return {
    query: queryView(input.result, (data) => data.photos.length === 0 && waiting.length === 0),
    tiles: [...pending, ...placed],
    pairs,
    problems: [...readProblems, ...profileProblems],
    decision: actionView(input.decision),
    adding: input.adding,
    sourcesError: input.sourcesError,
    hasSources: input.sources.some((source) => source.supports.add),
  }
}
